import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher/LanguageSwitcher";
import { getCurrentModerator, type ModeratorProfile, moderatorLogout } from "@/config/api";
import ModeratorNotificationBell from "./ModeratorNotificationBell";
import "./ModeratorLayout.css";

type ModeratorNavKey =
  | "dashboard"
  | "companies"
  | "verification"
  | "kyb"
  | "deals"
  | "coordinator"
  | "contracts"
  | "conversations"
  | "productFeedback"
  | "hsClassifier"
  | "dutyEstimate"
  | "articles"
  | "leadSources"
  | "readiness"
  | "billing"
  | "matching"
  | "pipelineRuns"
  | "moderators"
  | "analytics"
  | "profile";

interface NavItem {
  path: string;
  icon: string;
  labelKey: ModeratorNavKey;
  superOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/moderator/dashboard", icon: "fa-solid fa-gauge-high", labelKey: "dashboard" },
  { path: "/moderator/companies", icon: "fa-solid fa-building", labelKey: "companies" },
  { path: "/moderator/verification", icon: "fa-solid fa-shield-halved", labelKey: "verification" },
  { path: "/moderator/kyb", icon: "fa-solid fa-id-card-clip", labelKey: "kyb" },
  { path: "/moderator/deals", icon: "fa-solid fa-handshake", labelKey: "deals" },
  { path: "/moderator/coordinator", icon: "fa-solid fa-inbox", labelKey: "coordinator" },
  { path: "/moderator/contracts", icon: "fa-regular fa-file-lines", labelKey: "contracts" },
  { path: "/moderator/conversations", icon: "fa-regular fa-comment-dots", labelKey: "conversations" },
  { path: "/moderator/product-feedback", icon: "fa-solid fa-flag", labelKey: "productFeedback" },
  { path: "/moderator/hs-classifier", icon: "fa-solid fa-sitemap", labelKey: "hsClassifier" },
  { path: "/moderator/duty-estimate", icon: "fa-solid fa-calculator", labelKey: "dutyEstimate" },
  { path: "/moderator/articles", icon: "fa-regular fa-newspaper", labelKey: "articles" },
  { path: "/moderator/lead-sources", icon: "fa-solid fa-signs-post", labelKey: "leadSources" },
  { path: "/moderator/readiness", icon: "fa-solid fa-clipboard-check", labelKey: "readiness" },
  { path: "/moderator/billing", icon: "fa-solid fa-credit-card", labelKey: "billing" },
  { path: "/moderator/matching", icon: "fa-solid fa-sliders", labelKey: "matching", superOnly: true },
  { path: "/moderator/matching/runs", icon: "fa-solid fa-list-check", labelKey: "pipelineRuns", superOnly: true },
  { path: "/moderator/moderators", icon: "fa-solid fa-user-shield", labelKey: "moderators", superOnly: true },
  { path: "/moderator/analytics", icon: "fa-solid fa-chart-column", labelKey: "analytics" },
  { path: "/moderator/profile", icon: "fa-regular fa-user", labelKey: "profile" },
];

interface ModeratorLayoutProps {
  children: ReactNode | ((moderator: ModeratorProfile) => ReactNode);
}

const ModeratorLayout = ({ children }: ModeratorLayoutProps) => {
  const { t } = useTranslation(["layout", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const [moderator, setModerator] = useState<ModeratorProfile | null>(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Auth gate — every moderator page goes through this layout, so the gate
  // lives here once instead of in every page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await getCurrentModerator().catch(() => null);
      if (cancelled) return;
      if (!me) {
        navigate("/moderator/login", { replace: true });
        return;
      }
      setModerator(me);
      setAuthChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleNavClick = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await moderatorLogout();
    navigate("/moderator/login", { replace: true });
  };

  if (authChecking || !moderator) {
    return (
      <div className="moderator-layout moderator-layout--loading">
        <i className="fa-solid fa-spinner fa-spin" aria-hidden />
        <span>{t("common:states.loading")}</span>
      </div>
    );
  }

  const isSuper = moderator.role === "SUPERMODERATOR";
  const visibleNav = NAV_ITEMS.filter((item) => !item.superOnly || isSuper);

  // Pick the most-specific matching nav entry so nested paths
  // (e.g. /moderator/matching/runs) don't also light up the parent
  // (/moderator/matching).
  const activePath = visibleNav
    .filter((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
    .reduce<string | null>((best, item) => (best == null || item.path.length > best.length ? item.path : best), null);

  return (
    <div className="moderator-layout">
      <button
        type="button"
        className="moderator-layout__mobile-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label={t("nav.toggleMenu")}
        aria-expanded={isMobileMenuOpen}
      >
        <i className={isMobileMenuOpen ? "fa-solid fa-xmark" : "fa-solid fa-bars"} />
      </button>

      <aside className={`moderator-layout__sidebar ${isMobileMenuOpen ? "moderator-layout__sidebar--open" : ""}`}>
        <div className="moderator-layout__sidebar-header">
          <div className="moderator-layout__logo-mark">{t("moderator.logoMark")}</div>
          <span className="moderator-layout__logo-text">{t("moderator.console")}</span>
          <LanguageSwitcher />
          <ModeratorNotificationBell />
        </div>

        <nav className="moderator-layout__nav" aria-label={t("nav.moderatorNavigation")}>
          {visibleNav.map((item) => {
            const active = item.path === activePath;
            return (
              <button
                key={item.path}
                type="button"
                className={`moderator-layout__nav-item ${active ? "moderator-layout__nav-item--active" : ""}`}
                onClick={() => handleNavClick(item.path)}
                aria-current={active ? "page" : undefined}
              >
                <i className={item.icon} aria-hidden />
                <span>{t(`moderatorNav.${item.labelKey}`)}</span>
              </button>
            );
          })}
        </nav>

        <div className="moderator-layout__sidebar-footer">
          <button
            type="button"
            className="moderator-layout__user"
            onClick={() => handleNavClick("/moderator/profile")}
            aria-label={t("moderator.openProfile")}
          >
            <div className="moderator-layout__user-name">{moderator.fullName}</div>
            <div className="moderator-layout__user-role">
              {t("moderator.roleWithProfileLink", {
                role: isSuper ? t("moderator.roles.super") : t("moderator.roles.moderator"),
              })}
            </div>
          </button>
          <button type="button" className="moderator-layout__logout" onClick={handleLogout}>
            <i className="fa-solid fa-arrow-right-from-bracket" aria-hidden /> {t("moderator.signOut")}
          </button>
        </div>
      </aside>

      {isMobileMenuOpen && (
        <div
          className="moderator-layout__mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className="moderator-layout__main">
        <section className="moderator-layout__content">
          {typeof children === "function" ? children(moderator) : children}
        </section>
      </main>
    </div>
  );
};

export default ModeratorLayout;
